import { useLoader } from '../../../hooks/useLoader';
import { Controller, FormProvider } from 'react-hook-form';
import styles from './TestCollectionForm.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, MenuItem, Select } from '@dsi/react-eds';
import FileUploader from './FileUploader';
import { useState } from 'react';
import api from '../../../api/axiosInstance';
import { productService } from '../../../services/productService';
import { AxiosError } from 'axios';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useQuery } from '@tanstack/react-query';
import { useTestCollectionForm } from './test-collection-form';

function TestCollectionForm() {
  const { hideLoader, showLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const form = useTestCollectionForm();

  const [testCollectionFile, setTestCollectionFile] = useState<File>();
  const [testServices, setTestServices] = useState<{ id: string; name: string }[]>();
  const [productNumbers, setProductNumbers] = useState<
    { name: string; sw_product_no: string; release_note_no?: string; product_header?: string; webhook?: string }[]
  >([]);
  const [productLines, setProductLines] = useState<string[]>([]);

  const [disableTS, setDisableTS] = useState<boolean>(false);

  const aatTestServicesData = async () => {
    try {
      showLoader();
      const resp = await api.get('/api/test-collections/test-services');
      setTestServices(resp.data);
      return resp.data;
    } catch (error) {
      // show error
      addNotifications([
        {
          title: 'Error - No Test Services Found',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching AAT Test Services',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    }
  };
  useQuery({ queryKey: ['aatTestServices'], queryFn: aatTestServicesData, retry: 0, refetchOnWindowFocus: false });

  const swobProductLinesData = async () => {
    try {
      showLoader();
      const resp = await productService.getAllProductLines();
      const names = (resp.data || []).map((pl) => pl.name);
      setProductLines(names);
      return names;
    } catch (error: unknown) {
      // show error
      addNotifications([
        {
          title: 'Error - SWGW SWOB Product Lines List',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching SWGW SWOB Product Lines',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };
  useQuery({ queryKey: ['swobProductLines'], queryFn: swobProductLinesData, retry: 0, refetchOnWindowFocus: false });

  const swgwProductNumbers = async () => {
    try {
      showLoader();
      const resp = await api.get('/api/swgw-product');
      //const resp = { data: mockProductNumbers };
      setProductNumbers(resp.data.products);
      return resp.data.products;
    } catch (error: unknown) {
      setProductNumbers([]);
      // show error
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching test collections',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };
  useQuery({ queryKey: ['swgwProduct'], queryFn: swgwProductNumbers, retry: 0, refetchOnWindowFocus: false });

  const onSubmit = async () => {
    showLoader();
    if (testCollectionFile) {
      const request = new FormData();
      request.append('testCollectionName', form.getValues().testCollectionName);
      request.append('testServiceId', form.getValues().testService || '');
      request.append('productLine', form.getValues().productLine);
      request.append('productNumber', form.getValues().productNumber);
      request.append('productVersion', form.getValues().productVersion);
      request.append('testCollectionFile', testCollectionFile);
      try {
        const response = await api.post('/api/test-collections', request);
        console.log(response);
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-success',
            description: `Test Collection: ${form.getValues().testCollectionName} is now imported. You can use it in a Test Execution.`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
        resetAllForm();
        hideLoader();
      } catch (error) {
        console.error(error);
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while saving Test Collection',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        hideLoader();
      }
    }
  };

  const resetAllForm = () => {
    const pill: HTMLElement = document
      .querySelector('#TestCollectionDragAndDrop')
      ?.querySelector('.content')
      ?.querySelector('.file-pills')
      ?.querySelector('.pill') as HTMLElement;
    if (pill) {
      pill?.click();
    }
    form.reset();
  };

  const fileSelected = (file: File) => {
    setTestCollectionFile(file);
    const type = file.name.substring(file.name.lastIndexOf('.'));
    if (type === '.zip') {
      form.setValue('testService', '');
    }
    form.setValue('testCollectionFile', file.name);
    form.clearErrors('testCollectionFile');
  };

  const fileRemoved = () => {
    setTestCollectionFile(undefined);
    form.setValue('testCollectionFile', '');
    form.setError('testCollectionFile', { type: 'required', message: 'Test Collection File is required' });
  };

  return (
    <>
      <form autoComplete="off" onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
        <FormProvider {...form}>
          <div className={`${styles.formRow} row`}>
            <div className={`lg-6 ${styles.formInput}`}>
              <label htmlFor="testCollectionName" className={styles.whiteText}>
                Test Collection Name <span className={styles.requiredAsterisk}> *</span>{' '}
              </label>
              <Controller
                name="testCollectionName"
                control={form.control}
                render={({ field }) => (
                  <ReactEDSWrapper
                    className={form.formState.errors.testCollectionName ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                    component={Input}
                    id="testCollectionName"
                    type="text"
                    placeholder="Test Collection Name"
                    {...field}
                  />
                )}
              />
              {form.formState.errors.testCollectionName && (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {form.formState.errors.testCollectionName.message || 'Required'}
                </span>
              )}
            </div>
            <div className={`lg-6 ${styles.formInput}`}>
              <label htmlFor="testService" className={styles.whiteText}>
                Test Service {!disableTS && <span className={styles.requiredAsterisk}> *</span>}{' '}
              </label>
              <Controller
                name="testService"
                control={form.control}
                render={({ field }) => (
                  <ReactEDSWrapper
                    className={form.formState.errors.testService ? `${styles.selectError} ${styles.fullSelect}` : styles.fullSelect}
                    component={Select}
                    disabled={disableTS}
                    type="single"
                    id="testService"
                    key={JSON.stringify(field.value)}
                    value={field.value || []}
                    label="Select option"
                    onChange={(value) => {
                      field.onChange(Array.isArray(value) ? value.pop() : value);
                    }}
                  >
                    {testServices?.map((testService) => (
                      <ReactEDSWrapper component={MenuItem} key={testService.id} value={testService.id}>
                        {testService.name}
                      </ReactEDSWrapper>
                    ))}
                  </ReactEDSWrapper>
                )}
              />
              {form.formState.errors.testService && (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {form.formState.errors.testService.message || 'Required'}
                </span>
              )}
            </div>
          </div>
          <div className={`${styles.formRow} row`}>
            <div className={`lg-6 ${styles.formInput}`}>
              <label htmlFor="productLine" className={styles.whiteText}>
                Product Line <span className={styles.requiredAsterisk}> *</span>{' '}
              </label>
              <Controller
                name="productLine"
                control={form.control}
                render={({ field }) => (
                  <ReactEDSWrapper
                    className={form.formState.errors.productLine ? `${styles.selectError} ${styles.fullSelect}` : styles.fullSelect}
                    component={Select}
                    type="single"
                    id="productLine"
                    key={JSON.stringify(field.value)}
                    value={field.value || []}
                    label="Select option"
                    onChange={(value) => {
                      const val = Array.isArray(value) ? value.pop() : value;
                      field.onChange(val);
                      if (val.toLowerCase()?.includes('cloud ran')) {
                        setDisableTS(false);
                      } else {
                        setDisableTS(true);
                        form.setValue('testService', '');
                      }
                    }}
                  >
                    {productLines?.map((productLine) => (
                      <ReactEDSWrapper component={MenuItem} key={productLine} value={productLine}>
                        {productLine}
                      </ReactEDSWrapper>
                    ))}
                  </ReactEDSWrapper>
                )}
              />
              {form.formState.errors.productLine && (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {form.formState.errors.productLine.message || 'Required'}
                </span>
              )}
            </div>
            <div className={`lg-6 ${styles.formInput}`}>
              <label htmlFor="productNumber" className={styles.whiteText}>
                Product Number <span className={styles.requiredAsterisk}> *</span>{' '}
              </label>
              <Controller
                name="productNumber"
                control={form.control}
                render={({ field }) => (
                  <ReactEDSWrapper
                    className={form.formState.errors.productLine ? `${styles.selectError} ${styles.fullSelect}` : styles.fullSelect}
                    component={Select}
                    type="single"
                    id="productNumber"
                    key={JSON.stringify(field.value)}
                    value={field.value || []}
                    label="Select option"
                    onChange={(value) => {
                      field.onChange(Array.isArray(value) ? value.pop() : value);
                    }}
                  >
                    {productNumbers?.map((productNumber) => (
                      <ReactEDSWrapper
                        component={MenuItem}
                        key={productNumber.name}
                        value={productNumber.sw_product_no}
                      >{`${productNumber.sw_product_no} (${productNumber.name})`}</ReactEDSWrapper>
                    ))}
                  </ReactEDSWrapper>
                )}
              />
              {form.formState.errors.productNumber && (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {form.formState.errors.productNumber.message || 'Required'}
                </span>
              )}
            </div>
          </div>
          <div className={`${styles.formRow} row`}>
            <div className={`lg-6 ${styles.formInput}`}>
              <label htmlFor="productVersion" className={styles.whiteText}>
                Product Version <span className={styles.requiredAsterisk}> *</span>{' '}
              </label>
              <Controller
                name="productVersion"
                control={form.control}
                render={({ field }) => (
                  <ReactEDSWrapper
                    className={form.formState.errors.productVersion ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                    component={Input}
                    id="productVersion"
                    type="text"
                    placeholder="Products Version"
                    {...field}
                  />
                )}
              />
              {form.formState.errors.productVersion && (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {form.formState.errors.productVersion.message || 'Required'}
                </span>
              )}
            </div>
          </div>
        </FormProvider>
      </form>
      <div className={`${styles.formRow} row`}>
        <div className={`lg-12 ${styles.formInput}`}>
          <label htmlFor="fileUploader" className={styles.whiteText}>
            Test Collection File <span className={styles.requiredAsterisk}> *</span>{' '}
          </label>
          <FileUploader onFileSelected={fileSelected} onFileUnselected={fileRemoved} />
          {form.formState.errors.testCollectionFile && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">
                &#9888;
              </span>
              {form.formState.errors.testCollectionFile.message || 'Required'}
            </span>
          )}
        </div>
      </div>
      <div className={`${styles.formRow} row`}>
        <div className="lg-12">
          <ReactEDSWrapper
            id="addButton"
            component={Button}
            variant="secondary"
            className={`btn secondary ${styles.addButton}`}
            type="button"
            onClick={form.handleSubmit(onSubmit)}
          >
            <i className="icon icon-plus" /> Add
          </ReactEDSWrapper>
        </div>
      </div>
    </>
  );
}
export default TestCollectionForm;

// const mockProductNumbers = JSON.parse(`{
//     "success": true,
//     "message": "product(s) section read successfully.",
//     "products": [
//         {
//             "name": "gnodeB",
//             "sw_product_no": "CXP2010389/1",
//             "release_note_no": "",
//             "product_header": "gnodeB",
//             "webhook": ""
//         },
//         {
//             "name": "RLO",
//             "sw_product_no": "CXF1010442/1",
//             "release_note_no": "",
//             "product_header": "RLO",
//             "webhook": ""
//         },
//         {
//             "name": "purposeBuild",
//             "sw_product_no": "CXP9024418/22",
//             "release_note_no": "",
//             "product_header": "purposeBuild",
//             "webhook": ""
//         }
//     ]
// }`);
